import { BASE_URL } from '../components/AppConfig';

const getAuthToken = () => {
  const sessionToken = sessionStorage.getItem('sessionToken');
  return sessionToken ? `Bearer ${sessionToken}` : '';
};

const authFetch = async (path, options = {}) => {
  const token = getAuthToken();
  if (!token) {
    return { statusCode: '50000', message: 'Not authenticated' };
  }

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
        ...(options.headers || {}),
      },
      ...options,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok && !body?.statusCode) {
      return {
        statusCode: String(res.status),
        message: body?.message || res.statusText || 'Request failed',
        ...body,
      };
    }
    return body;
  } catch {
    return { statusCode: '50000', message: 'Request failed' };
  }
};

export const isAppLandingApiSuccess = (res) =>
  String(res?.statusCode || '') === '10000' || (res?.statusCode == null && res?.data != null);

export const getAppLandingPayload = (res) => res?.data ?? null;

const qs = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null || value === '') return;
    search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

/** GET /admin/app-landing/screens */
export const listAppLandingScreens = () =>
  authFetch('/admin/app-landing/screens', { method: 'GET' });

/** POST /admin/app-landing/screens */
export const createAppLandingScreen = (body) =>
  authFetch('/admin/app-landing/screens', {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** PATCH /admin/app-landing/screens/:screenKey */
export const patchAppLandingScreen = (screenKey, body) =>
  authFetch(`/admin/app-landing/screens/${encodeURIComponent(screenKey)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

/** GET /admin/app-landing/rules */
export const listAppLandingRules = () =>
  authFetch('/admin/app-landing/rules', { method: 'GET' });

/** GET /admin/app-landing/rules/:id */
export const getAppLandingRule = (id) =>
  authFetch(`/admin/app-landing/rules/${encodeURIComponent(id)}`, { method: 'GET' });

/** POST /admin/app-landing/rules */
export const createAppLandingRule = (body) =>
  authFetch('/admin/app-landing/rules', {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** PATCH /admin/app-landing/rules/:id */
export const patchAppLandingRule = (id, body) =>
  authFetch(`/admin/app-landing/rules/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

/** PATCH /admin/app-landing/rules/:id/enabled */
export const setAppLandingRuleEnabled = (id, isEnabled) =>
  authFetch(`/admin/app-landing/rules/${encodeURIComponent(id)}/enabled`, {
    method: 'PATCH',
    body: JSON.stringify({ is_enabled: isEnabled }),
  });

/** PUT /admin/app-landing/rules/:id/variants */
export const putAppLandingRuleVariants = (id, variants) =>
  authFetch(`/admin/app-landing/rules/${encodeURIComponent(id)}/variants`, {
    method: 'PUT',
    body: JSON.stringify(variants),
  });

/** DELETE /admin/app-landing/rules/:id */
export const deleteAppLandingRule = (id) =>
  authFetch(`/admin/app-landing/rules/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** GET /admin/app-landing/overrides */
export const listAppLandingOverrides = () =>
  authFetch('/admin/app-landing/overrides', { method: 'GET' });

/** PUT /admin/app-landing/overrides */
export const upsertAppLandingOverride = (body) =>
  authFetch('/admin/app-landing/overrides', {
    method: 'PUT',
    body: JSON.stringify(body),
  });

/** DELETE /admin/app-landing/overrides/:id */
export const deleteAppLandingOverride = (id) =>
  authFetch(`/admin/app-landing/overrides/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** POST /admin/app-landing/preview */
export const previewAppLanding = (body) =>
  authFetch('/admin/app-landing/preview', {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** GET /admin/app-landing/distribution */
export const getAppLandingDistribution = ({ appVersion, days } = {}) =>
  authFetch(
    `/admin/app-landing/distribution${qs({ app_version: appVersion, days })}`,
    { method: 'GET' }
  );

/** GET /admin/app-landing/stats */
export const getAppLandingStats = ({ days } = {}) =>
  authFetch(`/admin/app-landing/stats${qs({ days })}`, { method: 'GET' });

/** GET /admin/app-landing/audit */
export const getAppLandingAudit = ({ limit } = {}) =>
  authFetch(`/admin/app-landing/audit${qs({ limit })}`, { method: 'GET' });
